import type { JournalEntry, Notification, ATASpec, Project, Surcharge } from '../../lib/api/types'
import { ataNettoTotal, formatSEK } from '../../utils/calculations'
import { statusInfo } from '../../utils/statusUtils'

interface Props {
  journal: JournalEntry | null
  notification: Notification | null
  ataSpec: ATASpec | null
  project: Project | null
  surcharges: Surcharge[]
  sortOrder: number
}

/** Compact one-page "endast ÄTA" summary of the journal entry (#12356) —
 *  nr, rubrik, status, orsak, datum, ansvarig, påverkan, belopp. */
export default function ATASummaryPaper({ journal, notification, ataSpec, project, surcharges, sortOrder }: Props) {
  const nr = `ÄTA-${String(sortOrder).padStart(3, '0')}`
  const proj: Partial<Project> = project ?? {}
  const belopp = ataSpec ? ataNettoTotal(ataSpec.lineItems ?? [], surcharges) : null
  const status = journal?.status ?? 'upplagd'

  const rows: [string, string][] = [
    ['Status', statusInfo(status).label],
    ['Orsak', journal?.orsak || '–'],
    ['Ansvarig', journal?.ansvarig || '–'],
    ['Anmäld', notification?.datum || '–'],
    ['Påverkan tid', journal?.paverkanTid || 'Bedöms'],
    ['Påverkan ekonomi', journal?.paverkanEkonomi || 'Bedöms'],
    ['Belopp (exkl. moms)', belopp != null ? formatSEK(belopp) : '–'],
  ]

  return (
    <div className="paper">
      {/* Letterhead */}
      <div className="paper-letterhead">
        <div>
          <div className="paper-sender-name">TIBRAM BYGG AB</div>
          <div className="paper-sender-addr">
            {proj.adress && <>{proj.adress}<br /></>}
            {proj.postadress ?? ''}
          </div>
        </div>
        <div>
          <div className="paper-doc-type">ÄTA — sammanfattning</div>
          <div className="paper-doc-ref">{nr}</div>
          <div style={{ fontFamily: 'monospace', fontSize: '9pt', textAlign: 'right', color: '#666', marginTop: '3pt' }}>
            Projekt {proj.projektnr ?? '–'}
          </div>
        </div>
      </div>

      <hr className="paper-divider" />

      {/* Title */}
      <div style={{ marginBottom: '12pt' }}>
        <div style={{ fontSize: '8.5pt', textTransform: 'uppercase', letterSpacing: '.06em', color: '#888', marginBottom: '4pt' }}>Avseende</div>
        <div className="paper-avseende-title">{journal?.arbeteBeskrivning ?? nr}</div>
        <div style={{ fontSize: '10pt', color: '#555', marginTop: '3pt' }}>
          {proj.projektnr && proj.projektNamn
            ? `${proj.projektnr} · ${proj.projektNamn}${proj.ort ? ', ' + proj.ort : ''}`
            : proj.projektNamn ?? ''}
        </div>
      </div>

      {/* Key/value summary */}
      <table>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td style={{ width: '160pt', color: '#666', fontSize: '10pt' }}>{k}</td>
              <td style={{ fontSize: '10pt' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {journal?.arbeteDetaljer && (
        <div style={{ marginTop: '12pt' }}>
          <div className="paper-section-label">Beskrivning</div>
          <p style={{ whiteSpace: 'pre-wrap', fontSize: '10pt' }}>{journal.arbeteDetaljer}</p>
        </div>
      )}

      <div className="paper-footer">
        {nr} · {proj.projektnr ?? '–'} &nbsp;—&nbsp; Sida 1 (1)
      </div>
    </div>
  )
}
